import React, { useState } from 'react';
import { TrendingUp, DollarSign, Users, ShoppingBag, Package, BarChart3, Globe, Share2, Download } from 'lucide-react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

function MarketLATAM() {
  const [showShareMenu, setShowShareMenu] = useState(false);

  const generatePDF = async () => {
    const content = document.getElementById('market-report-content');
    if (!content) return;

    const canvas = await html2canvas(content, {
      scale: 2,
      useCORS: true,
      logging: false
    });
    
    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    });

    const imgWidth = 210;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    const pageHeight = 297;
    let heightLeft = imgHeight;
    let position = 0;

    pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
    heightLeft -= pageHeight;

    while (heightLeft > 0) {
      position = heightLeft - imgHeight;
      pdf.addPage();
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
    }

    pdf.save('LATAM-Market-Report-Motif Unbound.pdf');
  };

  const shareReport = (platform) => {
    const url = encodeURIComponent(window.location.href);
    const text = encodeURIComponent('Check out this LATAM Market Report from Motif Unbound');
    
    const shareUrls = {
      whatsapp: `https://wa.me/?text=${text}%20${url}`,
      twitter: `https://twitter.com/intent/tweet?text=${text}&url=${url}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${url}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${url}`
    };

    if (shareUrls[platform]) {
      window.open(shareUrls[platform], '_blank', 'width=600,height=400');
    }
    setShowShareMenu(false);
  };

  return (
    <div className="min-h-screen bg-white">
      <div id="market-report-content" className="max-w-6xl mx-auto px-6 py-16">
        <h1 className="text-5xl font-bold mb-6 text-[#000000]">
          Mexico and LATAM Market Overview
        </h1>
        
        <p className="text-xl text-gray-700 mb-12">
          High-growth markets with rising middle class, increasing digitalization, and strategic nearshoring opportunities.
        </p>

        <section className="grid md:grid-cols-3 gap-6 mb-12">
          <div className="bg-gradient-to-br from-[#38A3A5] to-[#38A3A5] text-white p-6 rounded-xl">
            <DollarSign size={32} className="mb-3" />
            <div className="text-3xl font-bold mb-2">$892B</div>
            <div className="text-sm opacity-90">Mexico Retail Sales 2025</div>
          </div>
          <div className="bg-gradient-to-br from-[#FFD965] to-[#f5cc4d] text-[#000000] p-6 rounded-xl">
            <TrendingUp size={32} className="mb-3" />
            <div className="text-3xl font-bold mb-2">+6.8%</div>
            <div className="text-sm opacity-90">LATAM Avg Growth Rate</div>
          </div>
          <div className="bg-gradient-to-br from-[#38A3A5] to-[#000000] text-white p-6 rounded-xl">
            <ShoppingBag size={32} className="mb-3" />
            <div className="text-3xl font-bold mb-2">$142B</div>
            <div className="text-sm opacity-90">Mexico E-commerce 2025</div>
          </div>
        </section>

        <section className="mb-12">
          <h2 className="text-3xl font-bold mb-6 text-[#000000]">2025 Retail Performance Highlights</h2>
          <div className="bg-gray-50 p-8 rounded-xl border border-gray-200">
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#38A3A5] rounded-lg flex items-center justify-center flex-shrink-0">
                  <BarChart3 size={24} className="text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2 text-[#000000]">Mexico: The Nearshoring Hub</h3>
                  <p className="text-gray-700 mb-2">Mexico has emerged as the premier nearshoring destination, offering USMCA benefits and strategic proximity to the US market.</p>
                  <ul className="list-disc list-inside text-sm text-gray-600 space-y-1">
                    <li>Manufacturing FDI increased 43% in 2024-2025</li>
                    <li>Zero tariffs on USMCA-compliant products</li>
                    <li>E-commerce growing at 28% annually</li>
                    <li>Rising middle class with $18,500 average household income</li>
                  </ul>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#FFD965] rounded-lg flex items-center justify-center flex-shrink-0">
                  <Globe size={24} className="text-[#000000]" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2 text-[#000000]">LATAM Digital Transformation</h3>
                  <p className="text-gray-700 mb-2">Latin America is experiencing rapid digital adoption, creating massive opportunities for e-commerce and DTC brands.</p>
                  <ul className="list-disc list-inside text-sm text-gray-600 space-y-1">
                    <li>470 million internet users across LATAM</li>
                    <li>E-commerce penetration reached 14.2% in 2025</li>
                    <li>Social commerce and mobile-first shopping dominate</li>
                    <li>Cross-border shopping growing 32% annually</li>
                  </ul>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#38A3A5] rounded-lg flex items-center justify-center flex-shrink-0">
                  <Users size={24} className="text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2 text-[#000000]">Growing Middle Class</h3>
                  <p className="text-gray-700 mb-2">Rising incomes and aspirational consumers driving demand for quality products and international brands.</p>
                  <ul className="list-disc list-inside text-sm text-gray-600 space-y-1">
                    <li>Middle class expected to reach 315 million by 2030</li>
                    <li>Premium product segments growing faster than mass market</li>
                    <li>Brand consciousness and quality preferences increasing</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="mb-12">
          <h2 className="text-3xl font-bold mb-6 text-[#000000]">Key Markets in LATAM</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white p-6 rounded-xl border-2 border-gray-200">
              <h3 className="text-xl font-bold mb-3 text-[#000000]">🇲🇽 Mexico</h3>
              <div className="text-2xl font-bold text-[#38A3A5] mb-2">$892B</div>
              <p className="text-gray-700 text-sm mb-3">2nd largest economy in LATAM, USMCA member</p>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• Population: 128 million</li>
                <li>• E-commerce: $142B (16% of retail)</li>
                <li>• Key sectors: Consumer goods, apparel, electronics</li>
                <li>• Strategic manufacturing hub</li>
              </ul>
            </div>

            <div className="bg-white p-6 rounded-xl border-2 border-gray-200">
              <h3 className="text-xl font-bold mb-3 text-[#000000]">🇧🇷 Brazil</h3>
              <div className="text-2xl font-bold text-[#38A3A5] mb-2">$1.2T</div>
              <p className="text-gray-700 text-sm mb-3">Largest market in LATAM with diverse consumer base</p>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• Population: 215 million</li>
                <li>• E-commerce: $187B (15.6% of retail)</li>
                <li>• Key sectors: Beauty, fashion, home goods</li>
                <li>• Mercado Libre dominates e-commerce</li>
              </ul>
            </div>

            <div className="bg-white p-6 rounded-xl border-2 border-gray-200">
              <h3 className="text-xl font-bold mb-3 text-[#000000]">🇦🇷 Argentina</h3>
              <div className="text-2xl font-bold text-[#38A3A5] mb-2">$276B</div>
              <p className="text-gray-700 text-sm mb-3">Sophisticated consumers with European influences</p>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• Population: 46 million</li>
                <li>• E-commerce: $32B (11.6% of retail)</li>
                <li>• Key sectors: Fashion, wine, technology</li>
                <li>• High brand awareness and quality focus</li>
              </ul>
            </div>

            <div className="bg-white p-6 rounded-xl border-2 border-gray-200">
              <h3 className="text-xl font-bold mb-3 text-[#000000]">🇨🇴 Colombia & 🇨🇱 Chile</h3>
              <div className="text-2xl font-bold text-[#38A3A5] mb-2">$285B</div>
              <p className="text-gray-700 text-sm mb-3">Emerging markets with strong growth potential</p>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• Combined population: 70 million</li>
                <li>• Fast-growing middle class</li>
                <li>• High digital adoption rates</li>
                <li>• Regional distribution hubs</li>
              </ul>
            </div>
          </div>
        </section>

        <section className="mb-12">
          <h2 className="text-3xl font-bold mb-6 text-[#000000]">Top Retail Categories 2025</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
              <h3 className="text-lg font-semibold mb-2">Fashion & Apparel - $156B</h3>
              <p className="text-gray-700 text-sm">Fast fashion and international brands dominate. Growing demand for sustainable and premium options.</p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
              <h3 className="text-lg font-semibold mb-2">Beauty & Personal Care - $89B</h3>
              <p className="text-gray-700 text-sm">LATAM's beauty market is one of the fastest-growing globally. Strong preference for natural and Brazilian brands.</p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
              <h3 className="text-lg font-semibold mb-2">Consumer Electronics - $76B</h3>
              <p className="text-gray-700 text-sm">Smartphones and accessories lead. Growing interest in smart home and wearable technology.</p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
              <h3 className="text-lg font-semibold mb-2">Home & Furniture - $64B</h3>
              <p className="text-gray-700 text-sm">Rising home ownership driving demand. Preference for affordable, stylish, and functional products.</p>
            </div>
          </div>
        </section>

        <section className="bg-gradient-to-r from-[#38A3A5] to-[#38A3A5] text-white p-8 rounded-2xl">
          <h2 className="text-2xl font-bold mb-4">Unlock LATAM's Growth Potential</h2>
          <p className="mb-6">From Mexico nearshoring to pan-LATAM e-commerce strategies, we help you navigate complex markets and capture growth opportunities across Latin America.</p>
          <div className="flex flex-wrap gap-4">
            <a
              href="mailto:contact@motifunbound.com?subject=Schedule%20a%20Consult&body=Hi%20Motif%20Unbound%20team,%0D%0A%0D%0AI%27d%20like%20to%20schedule%20a%20consultation%20to%20discuss%20our%20market%20expansion.%20Please%20share%20available%20times.%0D%0A%0D%0AThank%20you."
              className="bg-[#FFD965] hover:bg-[#f5cc4d] text-[#000000] px-8 py-3 rounded-lg font-semibold transition-all inline-flex items-center gap-2"
            >
              Schedule a Consult
            </a>
            <button 
              onClick={generatePDF}
              className="bg-white/10 hover:bg-white/20 border-2 border-white text-white px-8 py-3 rounded-lg font-semibold transition-all inline-flex items-center gap-2"
            >
              <Download size={20} />
              Download Market Report
            </button>
            <div className="relative">
              <button 
                onClick={() => setShowShareMenu(!showShareMenu)}
                className="bg-white/10 hover:bg-white/20 border-2 border-white text-white px-8 py-3 rounded-lg font-semibold transition-all inline-flex items-center gap-2"
              >
                <Share2 size={20} />
                Share Report
              </button>
              {showShareMenu && (
                <div className="absolute top-full mt-2 bg-white rounded-lg shadow-xl p-3 min-w-[200px] z-10">
                  <button
                    onClick={() => shareReport('whatsapp')}
                    className="w-full text-left px-4 py-2 hover:bg-gray-100 rounded text-gray-800 font-medium"
                  >
                    📱 WhatsApp
                  </button>
                  <button
                    onClick={() => shareReport('linkedin')}
                    className="w-full text-left px-4 py-2 hover:bg-gray-100 rounded text-gray-800 font-medium"
                  >
                    💼 LinkedIn
                  </button>
                  <button
                    onClick={() => shareReport('twitter')}
                    className="w-full text-left px-4 py-2 hover:bg-gray-100 rounded text-gray-800 font-medium"
                  >
                    🐦 Twitter
                  </button>
                  <button
                    onClick={() => shareReport('facebook')}
                    className="w-full text-left px-4 py-2 hover:bg-gray-100 rounded text-gray-800 font-medium"
                  >
                    👥 Facebook
                  </button>
                </div>
              )}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}

export default MarketLATAM;
